'use client'

import { useEffect, useState } from 'react'

/**
 * Hook to detect if the current device is primarily a touch device.
 * Returns `true` only for touch-primary devices (mobile, tablet).
 * Returns `false` for devices with cursor/pointer (desktop, laptop, hybrid with cursor as primary).
 * Returns `false` during SSR (before hydration).
 * 
 * For hybrid devices (touchscreen + cursor), this returns `false` because
 * the cursor is the primary input method.
 */
export function useIsTouchDevice(): boolean {
  const [isTouchDevice, setIsTouchDevice] = useState(false)

  useEffect(() => {
    const checkTouchDevice = () => {
      // Check if device has a cursor/pointer as primary input
      const hasPointer = window.matchMedia('(pointer:fine)').matches
      
      // If device has a fine pointer (cursor/mouse), it's not a touch-primary device
      if (hasPointer) {
        return false
      }

      // Otherwise, check for touch capability
      const nav = navigator as Navigator & { msMaxTouchPoints?: number }
      return (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        (nav.msMaxTouchPoints ?? 0) > 0
      )
    }

    setIsTouchDevice(checkTouchDevice())
  }, [])

  return isTouchDevice
}
